import React, { useState, useEffect } from 'react';

const LoadingScreen = ({ onComplete }) => {
  const [currentLine, setCurrentLine] = useState(0);
  const [currentProgress, setCurrentProgress] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [completedLines, setCompletedLines] = useState([]);

  const bootSequence = [
    { text: "BEATIVA v1.0.4 — booting", hasProgress: true, delay: 400 },
    { text: "Loading audio system", hasProgress: true, delay: 300 },
    { text: "Initializing YouTube API", hasProgress: true, delay: 300 },
    { text: "Starting terminal UI", hasProgress: true, delay: 300 },
    { text: "System ready", hasProgress: false, delay: 400 }
  ];

  useEffect(() => {
    let currentIndex = 0;
    let progressInterval;
    
    const processLine = () => {
      if (currentIndex >= bootSequence.length) {
        setTimeout(onComplete, 1000);
        return;
      }

      const line = bootSequence[currentIndex];
      setCurrentLine(currentIndex);
      setCurrentProgress('');

      if (line.hasProgress) {
        // Show progress dots
        let dots = 0;
        progressInterval = setInterval(() => {
          dots = (dots + 1) % 4;
          setCurrentProgress('.'.repeat(dots));
        }, 200);

        // Complete the line after delay
        setTimeout(() => {
          clearInterval(progressInterval);
          setCompletedLines(prev => [...prev, { ...line, status: getRandomStatus() }]);
          currentIndex++;
          setTimeout(processLine, 100);
        }, line.delay);
      } else {
        // No progress, just show the line
        setTimeout(() => {
          setCompletedLines(prev => [...prev, line]);
          currentIndex++;
          setTimeout(processLine, 100);
        }, line.delay);
      }
    };

    const timer = setTimeout(processLine, 500);

    const cursorTimer = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);

    return () => {
      clearTimeout(timer);
      clearInterval(progressInterval);
      clearInterval(cursorTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onComplete]);

  const getRandomStatus = () => {
    const statuses = ['[OK]', '[READY]', '[LOADED]', '[ACTIVE]', '[DONE]'];
    return statuses[Math.floor(Math.random() * statuses.length)];
  };

  return (
    <div className="fixed inset-0 bg-black text-white font-mono flex flex-col justify-center px-6 z-50 cursor-pointer" onClick={onComplete}>
      {/* Header Status Bar */}
      <div className="flex justify-between items-center mb-4 text-xs max-w-4xl mx-auto w-full">
        <div>
          <div><span className="text-gray-600">SYS_NAME</span><span className="ml-8 text-white">BEATIVA_v1.0.4</span></div>
          <div><span className="text-gray-600">SYS_AUTH</span><span className="ml-8 text-green-500">GUEST_ACCESS_GRANTED</span></div>
          <div><span className="text-gray-600">SYS_MODE</span><span className="ml-8 text-white">beativa.exe</span></div>
        </div>
        <div className="text-right">
          <div><span className="text-gray-600">UPTIME</span><span className="ml-4 text-white">21604 13:45</span></div>
          <div><span className="text-gray-600">TERMINAL</span><span className="ml-4 text-white">TTY0</span></div>
          <div><span className="text-gray-600">STATUS</span><span className="ml-4 text-red-400">200</span></div>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto w-full">
        <div className="space-y-0 text-xs">
          {/* Completed lines */}
          {completedLines.map((line, index) => (
            <div key={index} className="flex items-center">
              <span className="text-white">{line.text}</span>
              {line.status && (
                <span className="ml-2 text-green-500">{line.status}</span>
              )}
            </div>
          ))}
          
          {/* Current line with progress */}
          {currentLine < bootSequence.length && (
            <div className="flex items-center">
              <span className="text-white">{bootSequence[currentLine].text}</span>
              <span className="text-green-500">{currentProgress}</span>
              {showCursor && (
                <span className="ml-1 text-green-500 animate-blink">█</span>
              )}
            </div>
          )}
        </div>
        
        <div className="mt-6 text-center text-gray-600 text-xs">
          Click anywhere to skip loading...
        </div>
      </div>
    </div>
  );
};

const MatrixRain = ({ onExit }) => {
  const canvasRef = React.useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    const chars = 'ｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜﾝ0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const fontSize = 14;
    const columns = Math.floor(canvas.width / fontSize);
    
    // Randomize initial drop positions for natural start
    const drops = Array(columns).fill(0).map(() => Math.floor(Math.random() * -100));
    
    const draw = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx.fillStyle = '#0F0';
      ctx.font = `${fontSize}px monospace`;
      
      for (let i = 0; i < drops.length; i++) {
        const text = chars[Math.floor(Math.random() * chars.length)];
        ctx.fillText(text, i * fontSize, drops[i] * fontSize);
        
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
    };
    
    const interval = setInterval(draw, 33);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <div 
      className="fixed inset-0 bg-black z-50 cursor-pointer"
      onClick={onExit}
    >
      <canvas ref={canvasRef} className="w-full h-full" />
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-green-500 text-xs">
        Press ESC, Q, or click anywhere to exit
      </div>
    </div>
  );
};

const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState('');
  const [currentDate, setCurrentDate] = useState('');
  const [activeSection, setActiveSection] = useState('home');
  const [command, setCommand] = useState('');
  const [commandHistory, setCommandHistory] = useState([]);
  const [suggestion, setSuggestion] = useState('');
  const [matrixRunning, setMatrixRunning] = useState(false);
  const inputRef = React.useRef(null);

  const allCommands = ['download', 'features', 'about', 'home', 'clear', 'whoami', 'ls', 'date', 'uptime', 'neofetch', 'cowsay', 'matrix', 'hack', 'music', 'coffee', 'joke', 'fortune', 'help'];

  // Auto-focus input when clicking anywhere on the page
  useEffect(() => {
    const handleGlobalClick = () => {
      if (!isLoading && inputRef.current) {
        inputRef.current.focus();
      }
    };

    document.addEventListener('click', handleGlobalClick);
    return () => document.removeEventListener('click', handleGlobalClick);
  }, [isLoading]);

  // Auto-focus input after loading completes
  useEffect(() => {
    if (!isLoading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading]);

  // Arrow key navigation
  useEffect(() => {
    const sections = ['home', 'download', 'features', 'about'];
    
    const handleKeyDown = (e) => {
      if (matrixRunning) return;
      
      // Disable inspect shortcuts
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && e.key === 'I') ||
        (e.ctrlKey && e.shiftKey && e.key === 'J') ||
        (e.ctrlKey && e.shiftKey && e.key === 'C') ||
        (e.ctrlKey && e.key === 'U')
      ) {
        e.preventDefault();
        return false;
      }
      
      // Right arrow: autocomplete if typing, navigate if input is empty
      if (e.key === 'ArrowRight') {
        if (command.length > 0 && suggestion) {
          // Autocomplete
          e.preventDefault();
          setCommand(suggestion);
          setSuggestion('');
        } else {
          // Navigate to next section (when empty or no suggestion)
          e.preventDefault();
          const currentIndex = sections.indexOf(activeSection);
          const nextIndex = (currentIndex + 1) % sections.length;
          setActiveSection(sections[nextIndex]);
          setCommandHistory([]);
        }
      } else if (e.key === 'ArrowLeft') {
        // Navigate to previous section (always, unless actively editing cursor position)
        if (document.activeElement === inputRef.current && inputRef.current.selectionStart === 0) {
          e.preventDefault();
          const currentIndex = sections.indexOf(activeSection);
          const prevIndex = (currentIndex - 1 + sections.length) % sections.length;
          setActiveSection(sections[prevIndex]);
          setCommandHistory([]);
        } else if (command.length === 0) {
          e.preventDefault();
          const currentIndex = sections.indexOf(activeSection);
          const prevIndex = (currentIndex - 1 + sections.length) % sections.length;
          setActiveSection(sections[prevIndex]);
          setCommandHistory([]);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeSection, matrixRunning, command, suggestion]);

  // Disable right-click context menu
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    document.addEventListener('contextmenu', handleContextMenu);
    return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, []);

  useEffect(() => {
    // Fallback timeout to ensure loading doesn't get stuck
    const fallbackTimer = setTimeout(() => {
      setIsLoading(false);
    }, 8000); // 8 seconds max

    return () => clearTimeout(fallbackTimer);
  }, []);

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleTimeString('en-US', { 
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }));
      setCurrentDate(now.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\//g, '.'));
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleLoadingComplete = () => {
    setIsLoading(false);
  };

  const executeCommand = (cmd) => {
    const lowerCmd = cmd.toLowerCase().trim();
    setCommandHistory(prev => [...prev, `$ ${cmd}`]);
    
    switch(lowerCmd) {
      case 'help':
        setCommandHistory(prev => [...prev, {
          type: 'help',
          content: 'Available commands: download, features, about, home, clear, whoami, ls, date, uptime, neofetch, cowsay, matrix, hack, music, coffee, joke, fortune'
        }]);
        break;
      case 'download':
        setActiveSection('download');
        setCommandHistory(prev => [...prev, 'Opening download section...']);
        break;
      case 'features':
        setActiveSection('features');
        setCommandHistory(prev => [...prev, 'Loading features...']);
        break;
      case 'about':
        setActiveSection('about');
        setCommandHistory(prev => [...prev, 'Loading about section...']);
        break;
      case 'home':
        setActiveSection('home');
        setCommandHistory(prev => [...prev, 'Returning to home...']);
        break;
      case 'clear':
        setCommandHistory([]);
        break;
      case 'whoami':
        setCommandHistory(prev => [...prev, 'beativa-user', 'You are a music enthusiast exploring BEATIVA terminal player']);
        break;
      case 'ls':
        setCommandHistory(prev => [...prev, 'beativa.exe  features.txt  about.txt  downloads/  music/  playlists/']);
        break;
      case 'date':
        setCommandHistory(prev => [...prev, new Date().toString()]);
        break;
      case 'neofetch':
        setCommandHistory(prev => [...prev, 
          '      _______         OS: BEATIVA Terminal v1.0.4',
          '     /       \\        Host: Music Player System',
          '    |  O   O  |       Kernel: Audio-Engine-5.2',
          '    |    ^    |       Uptime: infinity (always vibing)',
          '    |  \\_____/|       Shell: beativa-sh',
          '     \\_______/        Terminal: TTY0',
          '        | |           CPU: Dual-Core Music Processor',
          '       /   \\          Memory: 50MB / Unlimited beats'
        ]);
        break;
      case 'cowsay':
        setCommandHistory(prev => [...prev,
          ' _____________________',
          '< BEATIVA rocks! >',
          ' ---------------------',
          '        \\   ^__^',
          '         \\  (oo)\\_______',
          '            (__)\\       )\\/\\',
          '                ||----w |',
          '                ||     ||'
        ]);
        break;
      case 'matrix':
        setMatrixRunning(true);
        setCommandHistory([]);
        break;
      case 'hack':
        setCommandHistory(prev => [...prev,
          'Initializing hack sequence...',
          '[####################] 100%',
          'Access granted to: Unlimited Music Library',
          'Downloading: All the vibes...',
          'Status: You are now a certified music hacker B-)'
        ]);
        break;
      case 'music':
        setCommandHistory(prev => [...prev,
          '>> Now Playing: Your Favorite Track <<',
          '[========>-----------] 2:45 / 4:20',
          '|< << || >> >|',
          'Volume: [||||||||||||--------] 75%',
          '',
          'Download BEATIVA to play real music!'
        ]);
        break;
      case 'coffee':
        setCommandHistory(prev => [...prev,
          '        (  (  ',
          '         )  ) ',
          '      ........',
          '      |      |]',
          '      |      |',
          '      |      |',
          '      |______|',
          '     /        \\',
          '    /__________\\',
          '',
          'Here is your coffee!',
          'Perfect for coding and listening to BEATIVA!'
        ]);
        break;
      case 'joke':
        const jokes = [
          'Why do programmers prefer dark mode? Because light attracts bugs! >_<',
          'Why did the developer go broke? Because he used up all his cache! :(',
          'How many programmers does it take to change a light bulb? None, thats a hardware problem! -_-',
          'Why do Java developers wear glasses? Because they dont C#! ^_^',
          'Whats a programmers favorite music player? BEATIVA, obviously! :D'
        ];
        setCommandHistory(prev => [...prev, jokes[Math.floor(Math.random() * jokes.length)]]);
        break;
      case 'fortune':
        const fortunes = [
          '* You will discover amazing music today with BEATIVA',
          '* A great playlist awaits you in the near future',
          '* Your coding session will be 10x better with good music',
          '* Today is a good day to download BEATIVA',
          '* The terminal gods smile upon your music taste',
          '* You will find the perfect song for your mood soon'
        ];
        setCommandHistory(prev => [...prev, fortunes[Math.floor(Math.random() * fortunes.length)]]);
        break;
      default:
        setCommandHistory(prev => [...prev, `Command not found: ${cmd}. Type 'help' for available commands.`]);
    }
    setCommand('');
  };

  const handleKeyPress = (e) => {
    if (matrixRunning && (e.key === 'Escape' || e.key === 'q' || (e.ctrlKey && e.key === 'c'))) {
      setMatrixRunning(false);
      setCommandHistory([]);
      return;
    }
    
    if (e.key === 'Enter') {
      executeCommand(command);
      setSuggestion('');
    } else if (e.key === 'Tab') {
      e.preventDefault();
      if (suggestion) {
        setCommand(suggestion);
        setSuggestion('');
      }
    }
  };

  const handleCommandChange = (e) => {
    const value = e.target.value;
    setCommand(value);
    
    // Find matching command for suggestion
    if (value.length > 0) {
      const match = allCommands.find(cmd => cmd.startsWith(value.toLowerCase()));
      setSuggestion(match || '');
    } else {
      setSuggestion('');
    }
  };

  const handleTabClick = (section) => {
    setActiveSection(section);
    setCommandHistory([]);
  };

  const renderContent = () => {
    switch(activeSection) {
      case 'download':
        return (
          <div className="mt-2">
            <div className="text-green-500 mb-2">$ ls -la /releases</div>
            <div className="border border-green-500 p-2 bg-black mb-3">
              <div className="flex justify-between items-center mb-1">
                <span className="font-bold">[WINDOWS_x64]</span>
                <span className="text-xs">BEATIVA_Setup_v1.0.0.exe</span>
              </div>
              <div className="text-xs mb-1">
                <span className="text-green-500">></span> v1.0-beta
              </div>
              <div className="text-xs mb-2">
                <span className="text-green-500">></span> Windows Installer Package
              </div>
              <div className="text-center">
                <a 
                  href="https://github.com/Shaarim24/Beativa-Music-Player/releases/download/v1.0.0-beta/BEATIVA_Setup_v1.0.0.exe"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-500 hover:bg-green-500 hover:text-black px-3 py-1 border border-green-500 transition-colors inline-block cursor-pointer text-xs"
                >
                  DOWNLOAD_BEATIVA_v1.0-BETA.exe
                </a>
                <div className="mt-2 text-xs text-yellow-500">
                  ⚠ BETA VERSION - May contain bugs and incomplete features
                </div>
                <div className="mt-1 text-xs text-gray-600">
                  © 2026 Shaarim. Proprietary Software - All Rights Reserved
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-600">
              <div><span className="text-green-500">></span> Run the installer and follow setup instructions</div>
              <div><span className="text-green-500">></span> Windows 10/11 64-bit required</div>
              <div><span className="text-green-500">></span> macOS and Linux versions coming soon...</div>
              <div className="mt-3 pt-2 border-t border-gray-800 text-xs">
                <div className="text-red-400 mb-1">⚠️ LEGAL NOTICE:</div>
                <div>• BEATIVA is proprietary software owned by Shaarim</div>
                <div>• This is a BETA release - use at your own risk</div>
                <div>• For licensing inquiries: shaarimalam888@gmail.com</div>
              </div>
            </div>
          </div>
        );
      
      case 'features':
        return (
          <div className="mt-2">
            <div className="text-green-500 mb-2">$ cat features.txt</div>
            <div className="space-y-0 text-xs">
              <div><span className="text-green-500">></span> YouTube music search and streaming</div>
              <div><span className="text-green-500">></span> Offline playlist downloads</div>
              <div><span className="text-green-500">></span> Terminal/CLI aesthetic design</div>
              <div><span className="text-green-500">></span> Minimal resource usage (~50MB)</div>
              <div><span className="text-green-500">></span> Audio quality selection</div>
              <div><span className="text-green-500">></span> Volume normalization</div>
              <div><span className="text-green-500">></span> Crossfade support</div>
              <div><span className="text-green-500">></span> Custom playlists and queue management</div>
            </div>
          </div>
        );
      
      case 'about':
        return (
          <div className="mt-2">
            <div className="text-green-500 mb-2">$ cat about.txt</div>
            <div className="space-y-2 text-xs">
              <p>BEATIVA is a lightweight, terminal-inspired music player built for developers and power users who appreciate minimalist design and efficient workflows.</p>
              <p>The application combines the functionality of modern music streaming with the aesthetic and efficiency of command-line interfaces.</p>
              <div className="mt-3">
                <div><span className="text-green-500">AUTHOR:</span> shaarim</div>
                <div><span className="text-green-500">VERSION:</span> 1.0-beta (early access)</div>
                <div><span className="text-green-500">PLATFORM:</span> Windows 10/11 (64-bit)</div>
                <div><span className="text-green-500">LICENSE:</span> Free (Beta)</div>
                <div>
                  <span className="text-green-500">CONTACT:</span> 
                  <a 
                    href="https://mail.google.com/mail/?view=cm&fs=1&to=shaarimalam888@gmail.com" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white hover:text-green-500 transition-colors cursor-pointer ml-1"
                  >
                    shaarimalam888@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        );
      
      default: // home
        return (
          <div className="mt-2">
            {/* ASCII Art Logo - Only on Home */}
            <div className="mb-4 text-gray-600 font-mono text-xs leading-none">
              <pre>{`
 ________  _______   ________  _________  ___  ___      ___ ________     
|\\   __  \\|\\  ___ \\ |\\   __  \\|\\___   ___\\\\  \\|\\  \\    /  /|\\   __  \\    
\\ \\  \\|\\  \\ \\   __/|\\ \\  \\|\\  \\|___ \\  \\_\\ \\  \\ \\  \\  /  / | \\  \\|\\  \\   
 \\ \\   __  \\ \\  \\_|/_\\ \\   __  \\   \\ \\  \\ \\ \\  \\ \\  \\/  / / \\ \\   __  \\  
  \\ \\  \\|\\  \\ \\  \\_|\\ \\ \\  \\ \\  \\   \\ \\  \\ \\ \\  \\ \\    / /   \\ \\  \\ \\  \\ 
   \\ \\_______\\ \\_______\\ \\__\\ \\__\\   \\ \\__\\ \\ \\__\\ \\__/ /     \\ \\__\\ \\__\\
    \\|_______|\\|_______|\\|__|\\|__|    \\|__|  \\|__|\\|__|/       \\|__|\\|__|
              `}</pre>
            </div>

            <div className="text-green-500 mb-2">$ whoami</div>
            <div className="mb-3 leading-relaxed">
              <p className="mb-1">Terminal-inspired music player designed for developers and power users.</p>
              <p className="mb-1">Lightweight YouTube streaming with offline library management.</p>
              <p className="mb-2">Currently shipping <span className="text-green-500">BEATIVA v1.0-beta</span> — a music player with terminal aesthetics.</p>
            </div>

            <div className="text-green-500 mb-2">$ cat status.txt</div>
            <div className="mb-3">
              <div><span className="text-gray-600">SUPPORT</span><span className="ml-8">Windows 10/11 x64</span></div>
              <div><span className="text-gray-600">FOCUS</span><span className="ml-8">Terminal UX — Music Streaming — Audio Management</span></div>
              <div><span className="text-gray-600">CONTACT</span><span className="ml-8">
                <a 
                  href="https://mail.google.com/mail/?view=cm&fs=1&to=shaarimalam888@gmail.com" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-500 hover:text-white transition-colors cursor-pointer"
                >
                  shaarimalam888@gmail.com
                </a>
              </span></div>
            </div>

            {/* Download Section */}
            <div className="border border-green-500 p-2 mb-3 bg-black cursor-pointer hover:bg-green-900 transition-all duration-300 ease-in-out"
                 onClick={() => handleTabClick('download')}>
              <span className="text-green-500">></span> BEATIVA v1.0-BETA IS LIVE — DOWNLOAD • WINDOWS • TERMINAL EDITION
            </div>

            <div className="text-gray-600 mb-4">
              TIP: ↑↓ arrows to navigate — type <span className="text-green-500">help</span> for commands
            </div>
          </div>
        );
    }
  };

  return (
    <>
      {isLoading && <LoadingScreen onComplete={handleLoadingComplete} />}
      {matrixRunning && <MatrixRain onExit={() => setMatrixRunning(false)} />}
      
      <div className="min-h-screen bg-black text-white font-mono pb-32">
        <div className="max-w-4xl mx-auto px-4 py-4">
          {/* Header Status Bar */}
          <div className="flex justify-between items-center mb-4 text-xs">
            <div>
              <div><span className="text-gray-600">SYS_NAME</span><span className="ml-8 text-white">BEATIVA_v1.0.0</span></div>
              <div><span className="text-gray-600">SYS_AUTH</span><span className="ml-8 text-green-500">GUEST_ACCESS_GRANTED</span></div>
              <div><span className="text-gray-600">SYS_MODE</span><span className="ml-8 text-white">beativa.exe</span></div>
            </div>
            <div className="text-right">
              <div><span className="text-gray-600">UPTIME</span><span className="ml-4 text-white">{currentDate} {currentTime}</span></div>
              <div><span className="text-gray-600">TERMINAL</span><span className="ml-4 text-white">TTY0</span></div>
              <div><span className="text-gray-600">STATUS</span><span className="ml-4 text-red-400">200</span></div>
            </div>
          </div>

          {/* Dynamic Content */}
          <div className="text-xs">
            {renderContent()}
          </div>

          {/* Command History */}
          {commandHistory.length > 0 && (
            <div className="mt-4 space-y-0 text-xs">
              {commandHistory.map((line, index) => {
                // Handle help command with special formatting
                if (typeof line === 'object' && line.type === 'help') {
                  const commands = line.content.replace('Available commands: ', '').split(', ');
                  const mainCommands = ['download', 'features', 'about', 'home', 'clear'];
                  
                  return (
                    <div key={index} className="text-gray-600">
                      Available commands:{' '}
                      {commands.map((cmd, i) => (
                        <span key={i}>
                          <span className={mainCommands.includes(cmd) ? 'text-green-500' : 'text-gray-600'}>
                            {cmd}
                          </span>
                          {i < commands.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </div>
                  );
                }
                
                // Handle ASCII art lines (preserve exact spacing)
                if (typeof line === 'string' && (line.includes('|') || line.includes('/') || line.includes('\\') || line.includes('_') || line.includes('('))) {
                  return (
                    <pre key={index} className={line.startsWith('$') ? 'text-green-500 font-mono' : 'text-gray-600 font-mono'} style={{ margin: 0, padding: 0 }}>
                      {line}
                    </pre>
                  );
                }
                
                // Handle regular string lines
                return (
                  <div key={index} className={typeof line === 'string' && line.startsWith('$') ? 'text-green-500' : 'text-gray-600'}>
                    {typeof line === 'object' ? line.content : line}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Fixed Bottom CLI Section - Only show when not loading */}
        {!isLoading && (
          <div className="fixed bottom-0 left-0 right-0 bg-black border-t border-gray-800 z-50">
            <div className="max-w-4xl mx-auto px-4 py-3">
              <div className="mb-2 flex items-center text-xs">
                <span className="text-green-500">beativa@beativa:~$ </span>
                <div className="relative flex-1 ml-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={command}
                    onChange={handleCommandChange}
                    onKeyDown={handleKeyPress}
                    className="bg-transparent border-none outline-none text-white w-full text-xs relative z-10"
                    placeholder="type a command (try: help)"
                    style={{ caretColor: 'white' }}
                  />
                  {suggestion && command && (
                    <div className="absolute top-0 left-0 text-gray-700 text-xs pointer-events-none">
                      <span className="invisible">{command}</span>{suggestion.slice(command.length)}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="text-gray-600 text-xs mb-2">
                <div>root@beativa/nav > SELECT MODULE [** ARROW + ENTER or CLICK]</div>
              </div>

              {/* Bottom Navigation Tabs */}
              <div className="flex space-x-0 text-xs mb-2">
                <button 
                  onClick={() => handleTabClick('home')}
                  className={`px-3 py-1 transition-colors ${
                    activeSection === 'home' 
                      ? 'bg-green-500 text-black' 
                      : 'text-gray-600 hover:text-green-500'
                  }`}>
                  > 01._HOME
                </button>
                <button 
                  onClick={() => handleTabClick('download')}
                  className={`px-3 py-1 transition-colors ${
                    activeSection === 'download' 
                      ? 'bg-green-500 text-black' 
                      : 'text-gray-600 hover:text-green-500'
                  }`}>
                  02._DOWNLOAD
                </button>
                <button 
                  onClick={() => handleTabClick('features')}
                  className={`px-3 py-1 transition-colors ${
                    activeSection === 'features' 
                      ? 'bg-green-500 text-black' 
                      : 'text-gray-600 hover:text-green-500'
                  }`}>
                  03._FEATURES
                </button>
                <button 
                  onClick={() => handleTabClick('about')}
                  className={`px-3 py-1 transition-colors ${
                    activeSection === 'about' 
                      ? 'bg-green-500 text-black' 
                      : 'text-gray-600 hover:text-green-500'
                  }`}>
                  04._ABOUT
                </button>
              </div>

              {/* Footer */}
              <div className="pt-2 border-t border-gray-800 text-center text-xs text-gray-600">
                <div>built by shaarim • terminal edition • 2026</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default App;
